import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import { Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { UserState } from '@/types/types';
import i18n from '@/i18n/i18n';
import { LanguageIcon, SearchIcon, SelectedIcon } from '../Shared/Icons';
import { setLanguage } from '@/features/user/userSlice';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuDropdown from '../Shared/MenuDropdown';
import MobilePortal from './MobilePortal';
import {
  appBar,
  btnContainer,
  btnIcon,
  companyContainer,
  langTitle,
  title,
  titleContainer,
  toolBar,
} from './navbar.style';
import SearchBar from '../Search/SearchBar';
import { RootState } from '@/app/store';
import MissingItemsLabel from '../Shared/MissingItemsLabel';

export default function Navbar() {
  const { t } = useTranslation();
  const { siteMetadata: siteIdMetadata, language }: UserState = useAppSelector(
    (state: RootState) => state.user,
  );
  const search = useAppSelector(
    (state: RootState) =>
      state.filters.assetTable.search || sessionStorage.getItem('params') || '',
  );
  const assetFiltered = useAppSelector(
    (state: RootState) => state.filters.assetTable.assetFiltered,
  );

  const selectedLanguage = language.includes('es') ? 'es' : 'en';

  const [activeSearch, setActiveSearch] = useState<boolean>(false);
  const [activeMobilePortal, setActiveMobilePortal] = useState<
    'language' | null
  >(null);

  const theme = useTheme<Theme>();

  const isAssetsPage = location.pathname.indexOf('assets') > -1;

  const dispatch = useAppDispatch();

  const [anchorElLanguage, setAnchorElLanguage] = useState<null | HTMLElement>(
    null,
  );

  const handleOpenLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (isDesktop) {
      setAnchorElLanguage(event.currentTarget);
    } else {
      setActiveMobilePortal('language');
    }
  };

  const handleCloseLanguageMenu = () => {
    setAnchorElLanguage(null);
  };

  const handleLanguage = (language: 'en' | 'es') => {
    i18n.changeLanguage(language);
    dispatch(setLanguage(language));
    setAnchorElLanguage(null);
  };

  const languages = [
    {
      name: t('sidenav.spanish'),
      onClick: handleLanguage,
      onClickParam: 'es',
      icon: selectedLanguage.includes('es') ? (
        <SelectedIcon
          fill={theme.palette.primary.main}
          data-testid="selected-icon"
        />
      ) : (
        ''
      ),
    },
    {
      name: t('sidenav.english'),
      onClick: handleLanguage,
      onClickParam: 'en',
      icon: selectedLanguage.includes('en') ? (
        <SelectedIcon
          fill={theme.palette.primary.main}
          data-testid="selected-icon"
        />
      ) : (
        ''
      ),
    },
  ];

  const isDesktop = useMediaQuery('(min-width:900px)');

  if (!siteIdMetadata) {
    return null;
  }

  return (
    <AppBar position="fixed" sx={appBar}>
      <Toolbar sx={toolBar}>
        <Box sx={companyContainer}>
          <Box
            component="img"
            data-testid="navbar-logo-image"
            alt={siteIdMetadata.name}
            width="40px"
            height="40px"
            marginRight="10px"
            src={
              siteIdMetadata.logoLocation === null
                ? 'https://storage.googleapis.com/cox2m-dealers-ui-logos/cog-57x57.png'
                : siteIdMetadata.logoLocation
            }
          />

          {(!isAssetsPage ||
            isDesktop ||
            (!activeSearch && search.length === 0)) && (
            <Box style={titleContainer}>
              <Typography sx={title}>{siteIdMetadata.name}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!isDesktop && isAssetsPage && (
            <Box sx={{ marginRight: '10px' }}>
              {!activeSearch && search.length === 0 ? (
                <Box
                  sx={btnIcon}
                  onClick={() => setActiveSearch(true)}
                  data-testid="mobile-search-button"
                  width="50px"
                >
                  <SearchIcon
                    viewBox="0 0 22 22"
                    color="action"
                    data-testid="search-button"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    marginBottom: { md: '12px' },
                    display: 'flex',
                    flex: !isDesktop ? 1 : '0 1 auto',
                    height: '40px',
                    width: '100%',
                    flexDirection: 'column',
                  }}
                >
                  <SearchBar search={search} />
                  {isAssetsPage &&
                    assetFiltered &&
                    assetFiltered.length !== 0 && (
                      <MissingItemsLabel
                        search={search}
                        items={assetFiltered}
                      ></MissingItemsLabel>
                    )}
                </Box>
              )}
            </Box>
          )}

          <Box sx={btnContainer}>
            <MenuDropdown
              anchorEl={anchorElLanguage}
              handleClose={handleCloseLanguageMenu}
              items={languages}
              mt="40px"
              ml="-10px"
              itemsPaddingRight="100px"
            />
            <Box
              onClick={handleOpenLanguageMenu}
              sx={btnIcon}
              data-testid="language-button"
              width="auto"
            >
              <LanguageIcon color="action" data-testid="language-button" />
              {isDesktop && (
                <Typography sx={langTitle}>
                  {selectedLanguage.toUpperCase()}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {activeMobilePortal === 'language' ? (
          <MobilePortal
            title={t('sidenav.languageSwitcher')}
            items={languages}
            setPortal={setActiveMobilePortal}
            setPortalValue={'settings'}
          />
        ) : null}
      </Toolbar>
    </AppBar>
  );
}
